/**
 * group-report-pdf (US-E23.F1-0789 · @UC9514) — phần THUẦN của hai báo cáo PDF cấp nhóm:
 * **Báo cáo thu** và **Báo cáo chi**. No DB, no framework, no pdfkit — worker chỉ nạp dữ liệu rồi
 * đưa qua đây để lấy ô chữ; chữ nghĩa (nhãn cột, vai, trạng thái) và phép định dạng tiền/ngày nằm
 * MỘT chỗ để bản vi và bản en không trôi khỏi nhau.
 *
 * 🔴 TIỀN LÀ BigInt MINOR, ĐỊNH DẠNG KHÔNG ĐI QUA FLOAT (§3.1). `formatReportAmount` chia nguyên
 * BigInt và ghép chuỗi — `Intl.NumberFormat.format(bigint)` nhận BigInt trực tiếp, không `Number()`.
 *
 * 🔴 KHÔNG có ô nào mang email/số điện thoại/địa chỉ: kiểu hàng chỉ có tên hiển thị + tên người đại
 * diện — cùng luật redaction với CSV (`group-report-csv.ts`). File PDF chia sẻ ra ngoài nhóm bằng
 * một cú kéo thả.
 *
 * ⚠️ Header có SỐ DƯ / TỔNG THU / TỔNG CHI (khác `activity` CSV cố ý không có số dư): route xuất cấp
 * nhóm là O/A/T-only — người giữ sổ — nên không có vai nào cần redaction ở đây. Mở route cho member
 * thường ⇒ header PHẢI đi qua `'•••'` trước, không được in số.
 */

import { minorUnitDigits } from "./currency-minor-units.js";

/** Ngôn ngữ của bản PDF — tập ĐÓNG, mirror `reportLocaleSchema` (contracts) + CHECK `report_jobs_locale_check`. */
export const REPORT_PDF_LOCALES = ["vi", "en"] as const;
export type ReportPdfLocale = (typeof REPORT_PDF_LOCALES)[number];

export function isReportPdfLocale(value: string | null | undefined): value is ReportPdfLocale {
  return value === "vi" || value === "en";
}

/**
 * Loại báo cáo PDF cấp nhóm — mirror `GROUP_REPORT_KIND` (contracts).
 *  · `finance` (US-E23.F1-0797, QĐ người dùng 16/09/2026): MỘT tệp gồm HAI phần — THU rồi CHI — có
 *    tiêu đề phần và vạch ngăn; đây là nút duy nhất còn trên mobile.
 *  · `income` / `expense`: bản tách (US-E23.F1-0789) GIỮ trong engine để job cũ ở "Bản xuất" vẫn
 *    đọc/tải lại được — không còn nút tạo mới.
 */
export const REPORT_PDF_KINDS = ["income", "expense", "finance"] as const;
export type ReportPdfKind = (typeof REPORT_PDF_KINDS)[number];

export function isReportPdfKind(value: string | null | undefined): value is ReportPdfKind {
  return typeof value === "string" && (REPORT_PDF_KINDS as readonly string[]).includes(value);
}

/**
 * Múi giờ ghi NGÀY trên PDF. Sản phẩm phục vụ nhóm tại Việt Nam và DB lưu UTC; in `createdAt` UTC
 * thô thì một khoản đóng lúc 23:30 giờ VN sẽ mang ngày hôm TRƯỚC — người đọc đối chiếu sao kê ngân
 * hàng sẽ thấy lệch. Cùng mặc định với `quiet-hours` / `batch-config` của worker.
 */
export const REPORT_PDF_TIMEZONE = "Asia/Ho_Chi_Minh";

/** Vai trong nhóm — mirror `GROUP_ROLE` (contracts; domain không import contracts). */
export type ReportMemberRole = "owner" | "admin" | "treasurer" | "member";

/** Trạng thái một dòng THU: đã vào sổ (3) hay mới là lời khai (2) — §3.7, KHÔNG gộp. */
export type IncomeRowStatus = "approved" | "awaiting";

/**
 * Nguồn của một dòng THU — quyết định cột "Khoản thu":
 *  · `collection`  — đóng đợt thu (`member_dues` ↔ `CONTRIBUTION`), có tên đợt + số kỳ nếu định kỳ;
 *  · `common_fund` — khoản "Khác" (`SPONSORSHIP_MEMBER` / `SPONSORSHIP_EXTERNAL` / `OTHER_INCOME`)
 *                    ⇒ in "Quỹ chung" (QĐ người dùng 15/09/2026);
 *  · `loan_repayment` — `LOAN_REPAYMENT` (E20, chưa có client) — có tên riêng để không bị gọi là "Quỹ chung".
 */
export type IncomeRowSource =
  | { readonly kind: "collection"; readonly title: string | null; readonly periodIndex: number | null }
  /**
   * Khoản "Khác" — cột "Khoản thu" in *"Quỹ chung <tên nhóm>"* cho cả ba; `origin` giữ LOẠI thu để
   * ô tên không rỗng khi khoản không gắn suất (review #793 · M1): `member` (đóng góp thêm của thành
   * viên) · `external` (tài trợ ngoài nhóm) · `other` (lãi/hoàn phí).
   */
  | { readonly kind: "common_fund"; readonly origin: "member" | "external" | "other" }
  | { readonly kind: "loan_repayment" };

export interface IncomeReportRow {
  /**
   * Tên suất (tên hiển thị, rơi về họ tên tài khoản chính). `null` khi khoản không gắn suất — ô tên
   * khi đó = ghi chú NGƯỜI GHI (`memo`) rồi mới tới nhãn loại thu; KHÔNG bịa tên người.
   */
  readonly memberName: string | null;
  /** Ghi chú người ghi (đã lọc câu mặc định cũ — `userIncomeMemo`); `null` khi không có. */
  readonly memo: string | null;
  /** Nhóm phụ huynh: họ tên NGƯỜI LỚN đại diện suất (đã nối bằng ", "). Nhóm thường ⇒ null. */
  readonly representative: string | null;
  /** Vai HIỂN THỊ của suất (suất do trưởng nhóm đứng tên ⇒ `owner`). Dòng không gắn suất ⇒ null. */
  readonly role: ReportMemberRole | null;
  readonly source: IncomeRowSource;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly status: IncomeRowStatus;
  readonly date: Date;
}

/** Nguồn của một dòng CHI. `expense` mang tên khoản chi; `loan_out` là cho vay (E20). */
export type ExpenseRowSource =
  | { readonly kind: "expense"; readonly title: string | null }
  | { readonly kind: "loan_out" };

export interface ExpenseReportRow {
  /**
   * NGƯỜI CHI: suất người ỨNG (`expenses.payerMemberId` — khai tường minh, KHÔNG suy từ người duyệt
   * §3.6); khoản quỹ trả trực tiếp (payer NULL) ⇒ người GHI khoản (`createdBy`) — tên + vai của chính
   * họ trong nhóm. Không ai ⇒ rỗng, không bịa.
   */
  readonly payerName: string;
  /**
   * KHÔNG có `representative` (QĐ người dùng 15/09/2026, sau bản in thật): người chi là NGƯỜI LỚN cầm
   * tiền — ở nhóm phụ huynh người ta không "đại diện" ai khi chi, nên cột đó trống suốt bảng và chỉ
   * khiến người đọc tưởng thiếu dữ liệu. Bảng thu vẫn có cột đại diện (suất con ↔ phụ huynh).
   */
  readonly role: ReportMemberRole | null;
  readonly source: ExpenseRowSource;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly date: Date;
}

/** Số liệu đầu trang — CÙNG nguồn với thẻ số dư của màn Nhóm (`sumGroupCashBalances` + `sumGroupIncomeExpense`). */
export interface ReportPdfHeader {
  readonly groupName: string;
  readonly currency: string;
  readonly balanceMinor: bigint;
  readonly totalIncomeMinor: bigint;
  readonly totalExpenseMinor: bigint;
  /** Mốc lập báo cáo (worker `now`). */
  readonly generatedAt: Date;
  /** Nhóm phụ huynh ⇒ có cột "Người đại diện". */
  readonly isParentGroup: boolean;
}

/** Từ điển chữ của PDF theo locale. Thêm ngôn ngữ = thêm MỘT entry, không sửa renderer. */
export interface ReportPdfText {
  readonly incomeTitle: string;
  readonly expenseTitle: string;
  /** Tiêu đề tệp gộp + tiêu đề hai PHẦN bên trong (US-E23.F1-0797). */
  readonly financeTitle: string;
  readonly incomeSection: string;
  readonly expenseSection: string;
  readonly groupLabel: string;
  readonly balanceLabel: string;
  readonly totalIncomeLabel: string;
  readonly totalExpenseLabel: string;
  readonly generatedAtLabel: string;
  readonly emptyRows: string;
  readonly totalRowLabel: string;
  readonly columns: {
    readonly index: string;
    readonly member: string;
    readonly payer: string;
    readonly representative: string;
    readonly role: string;
    readonly collection: string;
    readonly expense: string;
    readonly amount: string;
    readonly currency: string;
    readonly status: string;
    readonly date: string;
    /** US-E23.F1-0847 — cột của §2/§3 bản PDF mới. */
    readonly expectedAmount: string;
    readonly collectedAmount: string;
    readonly outstandingAmount: string;
    readonly period: string;
    readonly seat: string;
    readonly incomeKind: string;
    readonly contributor: string;
  };
  readonly roles: Readonly<Record<ReportMemberRole, string>>;
  readonly statuses: Readonly<Record<IncomeRowStatus, string>>;
  /** "Quỹ chung <tên nhóm>" — ghép từ tên nhóm HIỆN TẠI (QĐ người dùng 15/09/2026, US-E24.F50-0793). */
  readonly commonFund: (groupName: string) => string;
  readonly loanRepayment: string;
  /** Nhãn LOẠI thu cho ô tên khi khoản không gắn suất và không có ghi chú (review #793 · M1). */
  readonly incomeOrigin: Readonly<Record<"member" | "external" | "other", string>>;
  readonly loanOut: string;
  readonly untitledCollection: string;
  readonly untitledExpense: string;
  /** `Kỳ 3` / `Period 3` — ghép sau tên đợt định kỳ. */
  readonly period: (index: number) => string;
  /** Chân trang `Trang 1/3` / `Page 1/3`. */
  readonly page: (index: number, total: number) => string;

  /* ── US-E23.F1-0847 — BÁO CÁO TÀI CHÍNH NHÓM (bố cục mới, A4 dọc) ─────────────────────────── */
  /** Tiêu đề trang. KHÁC `financeTitle` (bản gộp cũ 0797) — bản mới là một thiết kế riêng. */
  readonly financeReportTitle: string;
  readonly tagline: string;
  readonly sectionExpenses: string;
  readonly sectionCollections: string;
  readonly sectionMemberStatus: string;
  readonly outstandingDetailTitle: string;
  readonly transferTitle: string;
  readonly transferContentLabel: string;
  readonly bankLabel: string;
  readonly accountNameLabel: string;
  readonly accountNumberLabel: string;
  /**
   * 🔴 QĐ-1b: câu nhắc KHÔNG được hứa "xác nhận tự động" — hệ thống KHÔNG đọc nội dung CK (webhook
   * nhận `memberDueId`, không nhận memo). In câu đó là app tự xưng một năng lực không có. AC13 khoá.
   */
  readonly transferNote: string;
  /** Dòng GỘP của §2.2 khi không tách được "Góp quỹ chung"/"Thu khác" (số ẩn / danh sách người khác đã bị lọc). */
  readonly offCycleIncomeLabel: string;
  /** QĐ-2 — hai mốc khác nhau phải nói rõ trên trang. */
  readonly scopeExpenseLabel: string;
  readonly scopeDebtLabel: string;
  readonly scopeAllTime: string;
  readonly scopeAsOfExport: string;
  /** Ô "Kỳ thu" của đợt một lần (AC4). QĐ người dùng 13/10/2026: "Một lần" thay "—" (dấu gạch đọc như thiếu dữ liệu). */
  readonly noPeriod: string;
  /**
   * 🔴 QĐ người dùng 13/10/2026 — 4 thẻ theo ảnh thiết kế (thay 3 thẻ Tổng thu · Tổng chi · Số dư của QĐ-A); Tổng chi + Số dư quỹ
   * xuống MỘT dòng ngay dưới thẻ. Câu phụ dưới số KHÔNG dùng "công nợ" (P5.3).
   */
  readonly cardActualIncome: string;
  readonly cardExpected: string;
  readonly cardExpectedSub: string;
  readonly cardCollected: string;
  readonly cardCollectedSub: string;
  readonly cardOutstanding: string;
  readonly cardOutstandingSub: string;
  readonly cardTotalExpense: string;
  readonly cardBalance: string;
  /** §2 — hai bảng con + thanh tổng (QĐ 13/10/2026: tách thu ngoài đợt). */
  readonly collectionSubTitle: string;
  readonly collectionTotalLabel: string;
  readonly offCycleSubTitle: string;
  readonly offCycleOtherLabel: string;
  readonly offCycleTotalLabel: string;
  readonly offCycleNote: string;
  readonly offCycleEmpty: string;
  /** Vế "Thu theo đợt" trong dòng công thức của Tổng thu thực tế. */
  readonly incomePartCollections: string;
  /** Khung cạnh §3 — ai góp quỹ chung. */
  readonly generalDetailTitle: string;
  readonly scopeRange: (from: string, to: string) => string;
  /**
   * 🔴 US-E23.F1-0867 (@UC0048) — bản của MEMBER thường: câu chú thích `'•••'` (in khi tài liệu có số bị ẩn) và câu dưới §3.2
   * khi dòng của người khác đã bị bỏ. KHÔNG thuật ngữ kế toán (P5.3); KHÔNG nói "bị khoá"/"không có quyền" — đây là lựa chọn
   * minh bạch của nhóm, không phải lỗi của người đọc.
   */
  readonly redactedLegend: string;
  readonly outstandingOthersHidden: string;
  /** Nhãn dòng "Góp quỹ chung" của §2.2 (tiền thành viên góp NGOÀI đợt — US-E23.F1-0869). */
  readonly generalContributionRow: string;
  /**
   * 🔴 US-E8.F1-0871 (@UC9581 · QĐ PO 30/09/2026) — câu dưới khung "Chi tiết góp quỹ chung" khi nhóm không công khai "ai đóng thế
   * nào" với người xuất: dòng của người khác KHÔNG in và dòng tổng ẩn (`'•••'`) — Σ các dòng còn lại không phải tổng của nhóm. In
   * THEO CẤU HÌNH (kể cả khi không dòng nào bị bỏ), cùng khuôn `outstandingOthersHidden`.
   */
  readonly memberOthersHidden: string;
}

/**
 * Nhãn vi/en. Chữ vi khớp ARB của `apps/mobile` (thẻ số dư: "Số dư quỹ" · "Tổng thu" · "Tổng chi";
 * vai: "Trưởng nhóm" · "Quản trị viên" · "Thủ quỹ" · "Thành viên") để PDF và màn hình gọi cùng tên
 * cho cùng một thứ.
 */
export const REPORT_PDF_TEXT: Readonly<Record<ReportPdfLocale, ReportPdfText>> = {
  vi: {
    incomeTitle: "BÁO CÁO THU",
    expenseTitle: "BÁO CÁO CHI",
    financeTitle: "BÁO CÁO THU CHI",
    incomeSection: "PHẦN THU",
    expenseSection: "PHẦN CHI",
    groupLabel: "Nhóm",
    balanceLabel: "Số dư quỹ",
    totalIncomeLabel: "Tổng thu",
    totalExpenseLabel: "Tổng chi",
    generatedAtLabel: "Ngày lập",
    emptyRows: "Chưa có khoản nào.",
    totalRowLabel: "Tổng cộng",
    columns: {
      index: "STT",
      member: "Thành viên",
      payer: "Người chi",
      representative: "Người đại diện",
      role: "Vai trò",
      collection: "Khoản thu",
      expense: "Khoản chi",
      amount: "Số tiền",
      currency: "Tiền tệ",
      status: "Trạng thái",
      date: "Ngày",
      expectedAmount: "Phải thu",
      collectedAmount: "Đã thu",
      outstandingAmount: "Còn thiếu",
      period: "Kỳ thu",
      seat: "Thành viên",
      incomeKind: "Loại thu",
      contributor: "Người đóng góp",
    },
    roles: { owner: "Trưởng nhóm", admin: "Quản trị viên", treasurer: "Thủ quỹ", member: "Thành viên" },
    statuses: { approved: "Đã duyệt", awaiting: "Chờ duyệt" },
    commonFund: (groupName) => `Quỹ chung ${groupName}`.trim(),
    loanRepayment: "Trả nợ vay",
    incomeOrigin: { member: "Đóng góp thêm", external: "Tài trợ bên ngoài", other: "Thu khác (lãi/hoàn phí)" },
    loanOut: "Cho vay",
    untitledCollection: "Đợt thu",
    untitledExpense: "Khoản chi",
    period: (index) => `Kỳ ${index}`,
    page: (index, total) => `Trang ${index}/${total}`,
    financeReportTitle: "BÁO CÁO TÀI CHÍNH NHÓM",
    tagline: "Quỹ nhóm minh bạch, ai cũng xem được",
    sectionExpenses: "1. Các khoản chi thực tế",
    sectionCollections: "2. Các khoản thu",
    sectionMemberStatus: "3. Tình trạng đóng quỹ (theo đợt thu)",
    outstandingDetailTitle: "3.2. Chi tiết khoản còn thiếu",
    transferTitle: "Thông tin chuyển khoản nhóm",
    transferContentLabel: "Nội dung chuyển khoản",
    bankLabel: "Ngân hàng",
    accountNameLabel: "Chủ tài khoản",
    accountNumberLabel: "Số tài khoản",
    // QĐ-1b — KHÔNG hứa "xác nhận tự động": hệ thống không đọc nội dung CK.
    transferNote: "Ghi đúng nội dung trên để thủ quỹ đối chiếu nhanh hơn.",
    offCycleIncomeLabel: "Thu ngoài đợt (quỹ chung, thu khác)",
    scopeExpenseLabel: "Khoản chi",
    // 🔴 P5.3 — KHÔNG "Công nợ" (review PR #930 · MEDIUM-2): đó là chữ kế toán, và trang này in cho
    // phụ huynh. Đo được: `grep "Công nợ"` trong `apps/mobile/lib` · ARB · `ui-kit` · `transparency`
    // ⇒ 0 kết quả, nên nếu để lọt thì đây là lần ĐẦU thuật ngữ đó chạm bề mặt người dùng cuối — và
    // nó vào bằng bản IN, thứ khó rút lại nhất. Câu trên giấy: "Khoản còn thiếu: Tại ngày xuất".
    scopeDebtLabel: "Khoản còn thiếu",
    scopeAllTime: "Toàn bộ",
    scopeAsOfExport: "Tại ngày xuất",
    noPeriod: "Một lần",
    cardActualIncome: "TỔNG THU THỰC TẾ",
    cardExpected: "TỔNG PHẢI THU (THEO ĐỢT)",
    cardExpectedSub: "Tổng mức phải đóng của thành viên",
    cardCollected: "ĐÃ THU (THEO ĐỢT)",
    cardCollectedSub: "Chưa bao gồm góp quỹ chung",
    cardOutstanding: "CÒN THIẾU (THEO ĐỢT)",
    cardOutstandingSub: "Tổng khoản còn thiếu của thành viên",
    cardTotalExpense: "Tổng chi",
    cardBalance: "Số dư quỹ",
    collectionSubTitle: "2.1 Thu theo đợt (có mức phải thu)",
    collectionTotalLabel: "Tổng thu theo đợt",
    offCycleSubTitle: "2.2 Thu ngoài đợt (không có mức phải thu)",
    offCycleOtherLabel: "Thu khác",
    offCycleTotalLabel: "Tổng thu ngoài đợt",
    offCycleNote: "Góp quỹ chung là khoản tự nguyện, không có mức phải thu và không làm giảm khoản còn thiếu của thành viên.",
    offCycleEmpty: "Chưa có khoản thu ngoài đợt.",
    incomePartCollections: "Thu theo đợt",
    generalDetailTitle: "Chi tiết góp quỹ chung",
    scopeRange: (from, to) => `${from} – ${to}`,
    redactedLegend: "•••: số nhóm không công khai với thành viên (theo cài đặt minh bạch của nhóm).",
    outstandingOthersHidden: "Nhóm không công khai danh sách còn thiếu của từng người — ở đây chỉ có khoản của bạn.",
    generalContributionRow: "Góp quỹ chung",
    memberOthersHidden: "Nhóm không công khai khoản góp của từng người — khoản góp quỹ chung của người khác không in ở đây, nên dòng tổng được ẩn.",
  },
  en: {
    incomeTitle: "INCOME REPORT",
    expenseTitle: "EXPENSE REPORT",
    financeTitle: "INCOME & EXPENSE REPORT",
    incomeSection: "INCOME",
    expenseSection: "EXPENSES",
    groupLabel: "Group",
    balanceLabel: "Fund balance",
    totalIncomeLabel: "Total income",
    totalExpenseLabel: "Total expenses",
    generatedAtLabel: "Generated on",
    emptyRows: "No entries yet.",
    totalRowLabel: "Total",
    columns: {
      index: "No.",
      member: "Member",
      payer: "Paid by",
      representative: "Representative",
      role: "Role",
      collection: "Collection",
      expense: "Expense",
      amount: "Amount",
      currency: "Currency",
      status: "Status",
      date: "Date",
      expectedAmount: "Expected",
      collectedAmount: "Collected",
      outstandingAmount: "Outstanding",
      period: "Period",
      seat: "Member",
      incomeKind: "Income type",
      contributor: "Contributor",
    },
    roles: { owner: "Owner", admin: "Admin", treasurer: "Treasurer", member: "Member" },
    statuses: { approved: "Approved", awaiting: "Pending approval" },
    commonFund: (groupName) => `General fund ${groupName}`.trim(),
    loanRepayment: "Loan repayment",
    incomeOrigin: { member: "Extra contribution", external: "External sponsorship", other: "Other income (interest/refund)" },
    loanOut: "Loan",
    untitledCollection: "Collection",
    untitledExpense: "Expense",
    period: (index) => `Period ${index}`,
    page: (index, total) => `Page ${index}/${total}`,
    financeReportTitle: "GROUP FINANCIAL REPORT",
    tagline: "Transparent group funds everyone can see",
    sectionExpenses: "1. Actual expenses",
    sectionCollections: "2. Income",
    sectionMemberStatus: "3. Contribution status (by collection)",
    outstandingDetailTitle: "3.2. Outstanding amounts in detail",
    transferTitle: "Group bank transfer details",
    transferContentLabel: "Transfer note",
    bankLabel: "Bank",
    accountNameLabel: "Account holder",
    accountNumberLabel: "Account number",
    transferNote: "Use the note above so the treasurer can reconcile faster.",
    offCycleIncomeLabel: "Income outside collections (general fund, other)",
    scopeExpenseLabel: "Expenses",
    scopeDebtLabel: "Outstanding",
    scopeAllTime: "All time",
    scopeAsOfExport: "As of export date",
    noPeriod: "One-time",
    cardActualIncome: "ACTUAL INCOME",
    cardExpected: "EXPECTED (COLLECTIONS)",
    cardExpectedSub: "Total members are asked to pay",
    cardCollected: "COLLECTED (COLLECTIONS)",
    cardCollectedSub: "General fund contributions not included",
    cardOutstanding: "OUTSTANDING (COLLECTIONS)",
    cardOutstandingSub: "Total members still have to pay",
    cardTotalExpense: "Total expenses",
    cardBalance: "Fund balance",
    collectionSubTitle: "2.1 Collections (with an expected amount)",
    collectionTotalLabel: "Total from collections",
    offCycleSubTitle: "2.2 Outside collections (no expected amount)",
    offCycleOtherLabel: "Other income",
    offCycleTotalLabel: "Total outside collections",
    offCycleNote: "General fund contributions are voluntary: they have no expected amount and do not reduce what a member still has to pay.",
    offCycleEmpty: "No income outside collections yet.",
    incomePartCollections: "Collections",
    generalDetailTitle: "General fund contributions",
    scopeRange: (from, to) => `${from} – ${to}`,
    redactedLegend: "•••: amount the group does not show to members (per the group's transparency settings).",
    outstandingOthersHidden: "The group keeps each person's outstanding amounts private — only yours are listed here.",
    generalContributionRow: "General fund contribution",
    memberOthersHidden:
      "The group keeps each person's contributions private — other members' general fund contributions are not listed here, so the total row is hidden.",
  },
};

function intlLocale(locale: ReportPdfLocale): string {
  // en-GB, KHÔNG en-US: ngày `dd/MM/yyyy` giống bản vi — người đọc VN mở bản tiếng Anh không phải
  // đoán 09/15 hay 15/09.
  return locale === "vi" ? "vi-VN" : "en-GB";
}

/**
 * Số tiền → chuỗi có dấu phân cách hàng nghìn theo locale, KHÔNG kèm ký hiệu tiền (cột "Tiền tệ"
 * đứng riêng). BigInt chia nguyên; phần lẻ ghép chuỗi — không float ở bất kỳ bước nào.
 * `1500000n, "VND"` ⇒ vi `1.500.000` · en `1,500,000`; `123456n, "USD"` ⇒ vi `1.234,56` · en `1,234.56`.
 */
export function formatReportAmount(amountMinor: bigint, currency: string, locale: ReportPdfLocale): string {
  const digits = minorUnitDigits(currency);
  const divisor = 10n ** BigInt(digits);
  const negative = amountMinor < 0n;
  const abs = negative ? -amountMinor : amountMinor;
  const major = abs / divisor;
  const fraction = abs % divisor;
  const grouped = new Intl.NumberFormat(intlLocale(locale), { useGrouping: true }).format(major);
  const decimalSeparator = locale === "vi" ? "," : ".";
  const tail = digits === 0 ? "" : `${decimalSeparator}${fraction.toString().padStart(digits, "0")}`;
  return `${negative ? "-" : ""}${grouped}${tail}`;
}

/** Ngày `dd/MM/yyyy` theo `REPORT_PDF_TIMEZONE` — cùng chữ số cho cả hai locale, chỉ khác ngôn ngữ nhãn. */
export function formatReportDate(value: Date, locale: ReportPdfLocale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: REPORT_PDF_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

/**
 * Nhãn "Khoản thu" của một dòng — MỘT chỗ cho luật "Khác ⇒ Quỹ chung <tên nhóm>" + "định kỳ ⇒ kèm
 * Kỳ n". `groupName` là tên nhóm LÚC LẬP báo cáo — cùng nghĩa với mọi bề mặt khác (feed, lịch sử
 * đóng góp): đổi tên nhóm ⇒ bản in sau đổi theo.
 */
export function incomeSourceLabel(source: IncomeRowSource, locale: ReportPdfLocale, groupName: string): string {
  const text = REPORT_PDF_TEXT[locale];
  switch (source.kind) {
    case "collection": {
      const title = source.title?.trim() || text.untitledCollection;
      return source.periodIndex === null ? title : `${title} · ${text.period(source.periodIndex)}`;
    }
    case "common_fund":
      return text.commonFund(groupName);
    case "loan_repayment":
      return text.loanRepayment;
  }
}

/** Nhãn "Khoản chi" của một dòng. */
export function expenseSourceLabel(source: ExpenseRowSource, locale: ReportPdfLocale): string {
  const text = REPORT_PDF_TEXT[locale];
  switch (source.kind) {
    case "expense":
      return source.title?.trim() || text.untitledExpense;
    case "loan_out":
      return text.loanOut;
  }
}

/**
 * Ô "Thành viên" của một dòng thu: tên suất → ghi chú người ghi → nhãn loại thu (khoản không gắn suất:
 * tài trợ ngoài, lãi/hoàn phí, trả nợ vay). Không bao giờ rỗng khi sổ cái có gì để nói.
 */
export function incomeSubjectLabel(row: Pick<IncomeReportRow, "memberName" | "memo" | "source">, locale: ReportPdfLocale): string {
  if (row.memberName) return row.memberName;
  if (row.memo?.trim()) return row.memo.trim();
  const text = REPORT_PDF_TEXT[locale];
  switch (row.source.kind) {
    case "common_fund":
      return text.incomeOrigin[row.source.origin];
    case "loan_repayment":
      return text.loanRepayment;
    case "collection":
      return "";
  }
}

export function roleLabel(role: ReportMemberRole | null, locale: ReportPdfLocale): string {
  return role === null ? "" : REPORT_PDF_TEXT[locale].roles[role];
}

/** Một bảng đã "dịch" xong: header + hàng ô chữ + cột nào canh phải. Renderer chỉ vẽ, không nghĩ. */
export interface ReportPdfTable {
  readonly title: string;
  readonly columns: readonly string[];
  /** Trọng số bề rộng từng cột (tỷ lệ, renderer chia theo bề rộng trang) — cùng độ dài với `columns`. */
  readonly columnWeights: readonly number[];
  /** Chỉ số cột canh phải (số tiền). */
  readonly rightAlignedColumns: readonly number[];
  readonly rows: readonly (readonly string[])[];
  /** Dòng tổng dưới bảng — `null` khi không có hàng. */
  readonly totalRow: readonly string[] | null;
}

/**
 * Bảng THU. Cột "Người đại diện" CHỈ xuất hiện ở nhóm phụ huynh — nhóm thường không có khái niệm đó,
 * một cột trống suốt bảng chỉ khiến người đọc tưởng dữ liệu thiếu.
 *
 * Dòng tổng cộng = Σ hàng `approved` (tiền đã VÀO sổ) — lời khai `awaiting` KHÔNG cộng (§3.7).
 * Nhiều loại tiền trong cùng nhóm ⇒ không cộng chéo: dòng tổng chỉ in khi mọi hàng cùng currency.
 */
export function buildIncomeTable(
  rows: readonly IncomeReportRow[],
  header: ReportPdfHeader,
  locale: ReportPdfLocale,
): ReportPdfTable {
  const text = REPORT_PDF_TEXT[locale];
  const c = text.columns;
  const columns = header.isParentGroup
    ? [c.index, c.member, c.representative, c.role, c.collection, c.amount, c.currency, c.status, c.date]
    : [c.index, c.member, c.role, c.collection, c.amount, c.currency, c.status, c.date];
  const columnWeights = header.isParentGroup
    ? [0.5, 2.1, 2.1, 1.2, 2.3, 1.4, 0.8, 1.3, 1.1]
    : [0.5, 2.4, 1.3, 2.8, 1.6, 0.9, 1.4, 1.2];
  const amountColumn = header.isParentGroup ? 5 : 4;
  const cells = rows.map((row, i) => {
    const base = [String(i + 1), incomeSubjectLabel(row, locale)];
    if (header.isParentGroup) base.push(row.representative ?? "");
    base.push(
      roleLabel(row.role, locale),
      incomeSourceLabel(row.source, locale, header.groupName),
      formatReportAmount(row.amountMinor, row.currency, locale),
      row.currency,
      text.statuses[row.status],
      formatReportDate(row.date, locale),
    );
    return base;
  });
  return {
    title: text.incomeTitle,
    columns,
    columnWeights,
    rightAlignedColumns: [amountColumn],
    rows: cells,
    totalRow: totalRowFor(
      rows.filter((r) => r.status === "approved").map((r) => ({ amountMinor: r.amountMinor, currency: r.currency })),
      rows.length,
      columns.length,
      amountColumn,
      locale,
    ),
  };
}

/**
 * Bảng CHI — cùng dòng tổng như bảng thu, nhưng KHÔNG có cột "Người đại diện" ở mọi loại nhóm
 * (QĐ người dùng 15/09/2026 — xem `ExpenseReportRow`). `header` vẫn nhận để hai bảng cùng chữ ký.
 */
export function buildExpenseTable(
  rows: readonly ExpenseReportRow[],
  _header: ReportPdfHeader,
  locale: ReportPdfLocale,
): ReportPdfTable {
  const text = REPORT_PDF_TEXT[locale];
  const c = text.columns;
  const columns = [c.index, c.payer, c.role, c.expense, c.amount, c.currency, c.date];
  const columnWeights = [0.5, 2.4, 1.3, 3.6, 1.7, 0.9, 1.2];
  const amountColumn = 4;
  const cells = rows.map((row, i) => [
    String(i + 1),
    row.payerName,
    roleLabel(row.role, locale),
    expenseSourceLabel(row.source, locale),
    formatReportAmount(row.amountMinor, row.currency, locale),
    row.currency,
    formatReportDate(row.date, locale),
  ]);
  return {
    title: text.expenseTitle,
    columns,
    columnWeights,
    rightAlignedColumns: [amountColumn],
    rows: cells,
    totalRow: totalRowFor(
      rows.map((r) => ({ amountMinor: r.amountMinor, currency: r.currency })),
      rows.length,
      columns.length,
      amountColumn,
      locale,
    ),
  };
}

/**
 * Dòng tổng: nhãn ở cột 2, số ở cột tiền, mã tiền ngay sau. `null` khi bảng rỗng hoặc lẫn nhiều
 * loại tiền (cộng VND với USD là một con số không có nghĩa — thà không in).
 */
function totalRowFor(
  amounts: readonly { amountMinor: bigint; currency: string }[],
  rowCount: number,
  columnCount: number,
  amountColumn: number,
  locale: ReportPdfLocale,
): readonly string[] | null {
  if (rowCount === 0) return null;
  const currencies = new Set(amounts.map((a) => a.currency));
  if (currencies.size > 1) return null;
  const currency = amounts[0]?.currency ?? null;
  const sum = amounts.reduce((s, a) => s + a.amountMinor, 0n);
  const row = new Array<string>(columnCount).fill("");
  row[1] = REPORT_PDF_TEXT[locale].totalRowLabel;
  row[amountColumn] = currency === null ? "" : formatReportAmount(sum, currency, locale);
  row[amountColumn + 1] = currency ?? "";
  return row;
}

/**
 * MỘT tài liệu PDF đã "dịch": tiêu đề tệp + các phần. Bản tách (thu HOẶC chi) có đúng một phần không
 * tiêu đề riêng; bản gộp `finance` (US-E23.F1-0797) có hai phần với tiêu đề "PHẦN THU"/"PHẦN CHI" —
 * renderer vẽ vạch ngăn + dải tiêu đề trước mỗi phần có `heading`.
 */
export interface ReportPdfDocument {
  readonly title: string;
  readonly sections: readonly { readonly heading: string | null; readonly table: ReportPdfTable }[];
  /** Σ hàng dữ liệu của mọi phần (không tính dòng tổng) — `report_jobs.rowCount`. */
  readonly rowCount: number;
}

/** Bản tách: một bảng, tiêu đề tệp = tiêu đề bảng. */
export function buildSingleTableDocument(table: ReportPdfTable): ReportPdfDocument {
  return { title: table.title, sections: [{ heading: null, table }], rowCount: table.rows.length };
}

/**
 * Bản GỘP thu chi (US-E23.F1-0797, QĐ người dùng 16/09/2026): THU trước, CHI sau, mỗi phần có tiêu
 * đề riêng để người đọc biết đang nhìn bảng nào; dòng tổng của từng phần giữ nguyên luật riêng
 * (thu: chỉ hàng đã duyệt; chi: mọi hàng).
 */
export function buildFinanceDocument(
  incomeRows: readonly IncomeReportRow[],
  expenseRows: readonly ExpenseReportRow[],
  header: ReportPdfHeader,
  locale: ReportPdfLocale,
): ReportPdfDocument {
  const text = REPORT_PDF_TEXT[locale];
  const income = buildIncomeTable(incomeRows, header, locale);
  const expense = buildExpenseTable(expenseRows, header, locale);
  return {
    title: text.financeTitle,
    sections: [
      { heading: text.incomeSection, table: income },
      { heading: text.expenseSection, table: expense },
    ],
    rowCount: income.rows.length + expense.rows.length,
  };
}

/** Các cặp nhãn–giá trị đầu trang, đã định dạng. */
export function buildHeaderLines(header: ReportPdfHeader, locale: ReportPdfLocale): readonly (readonly [string, string])[] {
  const text = REPORT_PDF_TEXT[locale];
  const money = (v: bigint) => `${formatReportAmount(v, header.currency, locale)} ${header.currency}`;
  return [
    [text.groupLabel, header.groupName],
    [text.balanceLabel, money(header.balanceMinor)],
    [text.totalIncomeLabel, money(header.totalIncomeMinor)],
    [text.totalExpenseLabel, money(header.totalExpenseMinor)],
    [text.generatedAtLabel, formatReportDate(header.generatedAt, locale)],
  ];
}
